import React, { Fragment, useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import PurchaseDrugEdit from "./PurchaseDrugEdit";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../../api/ConfirmDialog";
import usePurchaseDelete from "../../../../api/usePurchaseDelete";
import { useNavigate } from "react-router-dom";

const PurchaseDrugList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const pharmacy = auth?.user?.user?.pharmacy_type;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPurchases, setTotalPurchases] = useState(0);

  const {
    handleDelete: handleDeletePurchase,
    confirmDialogOpen,
    setConfirmDialogOpen,
    confirmDelete,
  } = usePurchaseDelete("purchases", token);

  const {
    handleDelete: handleDeletePurchaseItem,
    confirmDialogOpen: confirmDialogOpenItem,
    setConfirmDialogOpen: setConfirmDialogOpenItem,
    confirmDelete: confirmDeleteItem,
  } = usePurchaseDelete("purchase-items", token);

  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObjects(
      ["purchases"],
      `purchases/?purchase_items__pharmacy=${pharmacy}&search=${searchQuery}&page=${page}&page_size=${rowsPerPage}`,
      token
    );

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPurchases(data.count);
    }
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const getApprovalStatusLabel = (status) => {
    if (status === 1) return t("pharmacy.pending");
    if (status === 2) return t("pharmacy.approved");
    if (status === 3) return t("pharmacy.rejected");
    return t("pharmacy.unknownStatus");
  };

  const getApprovalStatusColor = (status) => {
    if (status === 1) return "warning";
    if (status === 2) return "success";
    if (status === 3) return "error";
    return "default";
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  if (isLoading) {
    return <Typography>{t("pharmacy.loading")}</Typography>;
  }

  if (isError) {
    return <Typography>{error.message}</Typography>;
  }

  if (isSuccess) {
    const purchases = data?.results || [];

    return (
      <Fragment>
        <Box
          my={4}
          display="flex"
          flexDirection={isSmallScreen ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isSmallScreen ? "stretch" : "center"}
          mb={3}
          gap={isSmallScreen ? 2 : 0}
          width={"100%"}
        >
          <Typography variant="h5" fontWeight="bold" color="primary" mb={2}>
            {t("pharmacy.purchaseList")}
          </Typography>
          {/* Add New Button */}
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/pharmacy/purchase-drug/add")}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              paddingX: 3,
              paddingY: isSmallScreen ? 1 : 1.5,
              alignSelf: isSmallScreen ? "flex-end" : "center",
              borderRadius: "8px",
              boxShadow: 3,
              transition: "all 0.3s ease",
              backgroundColor: "#007174",
              "&:hover": {
                backgroundColor: "#007174",
                boxShadow: 6,
                transform: "translateY(-2px)",
              },
              "&:active": {
                boxShadow: 2,
                transform: "translateY(0px)",
              },
              "& .MuiButton-startIcon": {
                marginRight: 1,
              },
            }}
          >
            {t("pharmacy.addNew")}
          </Button>
        </Box>

        <Box mb={1}>
          {/* Search Bar */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Box>

        <Grid container spacing={3}>
          {purchases.length > 0 ? (
            purchases.map((purchase, index) => {
              const status = purchase.is_approved;
              return (
                <Grid item xs={12} md={6} key={purchase.id}>
                  <Paper
                    elevation={status === 2 ? 2 : 4}
                    sx={{
                      mb: 3,
                      padding: "24px",
                      borderRadius: "12px",
                      width: "100%",
                      boxShadow:
                        status === 2
                          ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                          : "0px 8px 24px rgba(255, 0, 0, 0.3)",
                      backgroundColor:
                        status === 2
                          ? "#f9f9f9"
                          : status === 3
                          ? "#ffebee"
                          : "#fffde7",
                      borderLeft:
                        status === 3
                          ? "4px solid #d32f2f"
                          : status === 1
                          ? "4px solid #fbc02d"
                          : "none",
                    }}
                  >
                    {/* Purchase Header */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="primary"
                          sx={{
                            fontSize: isSmallScreen ? "1rem" : "1.25rem",
                          }}
                        >
                          {t("pharmacy.purchase")} #{purchase.id} -{" "}
                          {purchase.date}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{
                            fontSize: isSmallScreen ? "0.9rem" : "1rem",
                            mt: 1,
                          }}
                        >
                          {t("pharmacy.medicineCompany")}:{" "}
                          {purchase.medicine_company_display}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          fontSize={isSmallScreen ? "0.8rem" : "0.9rem"}
                        >
                          <strong>{t("pharmacy.totalAmount")}:</strong>{" "}
                          {purchase.total_amount} ؋
                        </Typography>
                        <Chip
                          label={getApprovalStatusLabel(purchase.is_approved)}
                          color={getApprovalStatusColor(purchase.is_approved)}
                          sx={{
                            fontWeight: "bold",
                            fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                          }}
                        />
                      </Stack>
                    </Box>

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    {/* Accordion for Purchase Details */}
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography
                          fontWeight="bold"
                          color={
                            status === 3
                              ? "error.main"
                              : status === 1
                              ? "warning.main"
                              : "text.primary"
                          }
                        >
                          {t("pharmacy.medicinesInPurchase")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {purchase.items && purchase.items.length > 0 ? (
                          <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 650 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="center">
                                    {t("pharmacy.category")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.medicine")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.quantity")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.purchasePrice")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.sellPrice")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.expiryDate")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {t("pharmacy.actions")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {purchase.items.map((item) => (
                                  <TableRow
                                    key={item.id}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "#f0f4f8",
                                      },
                                    }}
                                  >
                                    <TableCell align="center">
                                      <Typography>
                                        {item.category_display}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography>
                                        {item.medicine_display}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography>{item.quantity}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography>
                                        {item.purchase_price} ؋
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography>
                                        {item.sell_price} ؋
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography>
                                        {item.expiry_date}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      {status === 1 && (
                                        <>
                                          <Tooltip
                                            title={t("pharmacy.editItem")}
                                          >
                                            <IconButton
                                              color="primary"
                                              onClick={() => handleOpen(item)}
                                            >
                                              <EditIcon />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip
                                            title={t("pharmacy.deleteItem")}
                                          >
                                            <IconButton
                                              color="error"
                                              onClick={() =>
                                                handleDeletePurchaseItem(
                                                  item.id
                                                )
                                              }
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        ) : (
                          <Typography>{t("pharmacy.noMedicines")}</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box textAlign="center" mt={5}>
                <Typography variant="h6">
                  {t("pharmacy.noMedicineOrder")}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Pagination Controls */}
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={3}
              mb={5}
            >
              <Box display="flex" alignItems="center">
                <Typography>{t("pharmacy.rowsPerPage")}: </Typography>
                <FormControl
                  sx={{
                    ml: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                  size="small"
                >
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Pagination
                count={Math.ceil(totalPurchases / rowsPerPage)}
                page={page}
                onChange={handlePageChange}
                variant="outlined"
                color="primary"
              />
            </Box>
          </Grid>

          {/* Confirm Dialogs */}
          <ConfirmDialog
            confirmDialogOpen={confirmDialogOpen}
            setConfirmDialogOpen={setConfirmDialogOpen}
            confirmDelete={confirmDelete}
          />
          <ConfirmDialog
            confirmDialogOpen={confirmDialogOpenItem}
            setConfirmDialogOpen={setConfirmDialogOpenItem}
            confirmDelete={confirmDeleteItem}
          />

          {/* Edit Dialog */}
          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
              <Typography fontWeight="bold" color="primary">
                {t("pharmacy.editPurchaseItem")}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ padding: "20px" }}>
              {selectedItem && (
                <PurchaseDrugEdit
                  purchaseItem={selectedItem}
                  onClose={handleClose}
                  token={token}
                />
              )}
            </DialogContent>
          </Dialog>
        </Grid>
      </Fragment>
    );
  }
};

export default PurchaseDrugList;
